import { BoxProps, Stack } from '@chakra-ui/react'
import {
  ASSESSMENTS,
  BaseInsuranceCoverage,
  FieldMap,
  FieldMapValue,
  FileDBValue,
  getPregnancyCoveragePath,
  insuranceCardField,
  ListField,
  mergeData,
  OnUploadProgress,
  planDocumentsField,
  PregnancyCoverageLabel,
} from '@hb/shared'

import { set } from 'nested-property'
import { useCallback, useMemo } from 'react'

import { ThemeContext, useApp, useAuth } from '@hb/react'
import { processListFieldData } from '../../backend/db/utils/processData'
import { useProfile } from '../../contexts/user/ProfileContext'
import { updateAssessmentAnswers } from '../../utils'
import { DataView, DataViewList } from '../DataView/DataView'

const insuranceCardBoxProps: BoxProps = {
  borderBottom: 'none',
}
export const InsuranceCardView = ({
  data,
  corrections,
  updateField,
  baseStoragePath,
  field,
  adminView,
}: {
  data: FieldMapValue | undefined
  corrections: FieldMapValue | undefined
  updateField: (path: string, data: any) => Promise<any>
  baseStoragePath: string
  field: FieldMap
  adminView: boolean
}) => (
  <DataView
    initExpanded={!!(data ?? corrections)}
    updateField={updateField}
    baseStoragePath={baseStoragePath}
    field={field}
    boxProps={insuranceCardBoxProps}
    data={data}
    corrections={corrections}
    adminView={adminView}
  />
)

export const CoverageFilesView = ({ adminView }: { adminView: boolean }) => {
  const {
    selectedAssessment: { populated: selectedAssessment, id: assessmentId },
    // selectedAssessment: { ref, populated: selectedAssessment },
    // assessmentId,
  } = useProfile()
  const { appName } = useApp()
  const user = useAuth(s => s.authUser)

  // const { mergedData } = selectedAssessment ?? {}
  // const { primaryCoverage, medicaidCoverage } = mergedData?.insuranceInfo ?? {}
  // const { insurancePlans } = user ?? {}
  const { plans, data, corrections } = selectedAssessment ?? {}
  const { primary: primaryFromPlans, secondary: secondaryFromPlans } = plans ?? {}

  const primaryData = useMemo(
    () =>
      mergeData(primaryFromPlans, data?.insuranceInfo?.primaryCoverage) as BaseInsuranceCoverage,
    [primaryFromPlans, data],
  )
  const secondaryData = useMemo(
    () =>
      mergeData(
        secondaryFromPlans,
        data?.insuranceInfo?.secondaryCoverage,
      ) as BaseInsuranceCoverage,
    [secondaryFromPlans, data],
  )

  const primaryCorrections = useMemo(
    () => corrections?.insuranceInfo?.primaryCoverage,
    [corrections],
  )
  const secondaryCorrections = useMemo(
    () => corrections?.insuranceInfo?.secondaryCoverage,
    [corrections],
  )

  // const patientStoragePath = useMemo(
  //   () => `${isInvite ? USER_INVITES : USERS}/${userId}`,
  //   [isInvite, userId],
  // )

  const primaryCoverageInsuranceCardField = useMemo<FieldMap>(
    () => ({
      ...insuranceCardField,
      name: 'Primary Insurance Card',
      initExpanded: adminView
        ? !!(primaryData?.insuranceCard ?? primaryCorrections?.insuranceCard)
        : !!primaryData?.insuranceCard,
    }),
    [primaryData, primaryCorrections, adminView],
  )

  const primaryCoverageDocumentsField = useMemo<ListField>(
    () => ({
      ...planDocumentsField,
      name: 'Primary Coverage Documents',
      initExpanded: adminView
        ? !!(primaryData?.planDocuments ?? primaryCorrections?.planDocuments)
        : !!primaryData?.planDocuments,
    }),
    [primaryData, primaryCorrections, adminView],
  )

  const secondaryCoverageInsuranceCardField = useMemo<FieldMap>(
    () => ({
      ...insuranceCardField,
      name: 'Secondary Insurance Card',
      initExpanded: adminView
        ? !!(secondaryData?.insuranceCard ?? secondaryCorrections?.insuranceCard)
        : !!secondaryData?.insuranceCard,
    }),
    [secondaryData, secondaryCorrections, adminView],
  )

  const baseStoragePath = useMemo(() => {
    if (adminView) {
      return `${ASSESSMENTS}/${assessmentId}/corrections/insuranceInfo`
    }
    return `${ASSESSMENTS}/${assessmentId}/data/insurancePlans`
  }, [adminView, assessmentId])

  const secondaryCoverageDocumentsField = useMemo<ListField>(
    () => ({
      ...planDocumentsField,
      name: 'Secondary Coverage Documents',
      initExpanded: adminView
        ? !!(secondaryData?.planDocuments ?? secondaryCorrections?.planDocuments)
        : !!secondaryData?.planDocuments,
    }),
    [secondaryData, secondaryCorrections, adminView],
  )
  const handleInsuranceCardUpload = useCallback(
    async (coverageId: PregnancyCoverageLabel, key: string, updated: any) => {
      const uid = user?.uid ?? ''
      if (assessmentId ?? uid) {
        const dataPath = `${getPregnancyCoveragePath(coverageId)}.insuranceCard.${key}`

        const submitted = {}
        set(submitted, dataPath, updated)
        return updateAssessmentAnswers({ id: assessmentId, data: submitted, appName })
      }
      return { error: 'Unknown error' }
    },
    [user, assessmentId, appName],
  )

  const handleUpdatePlanDocuments = useCallback(
    async (
      coverageId: PregnancyCoverageLabel,
      updated: Array<FileDBValue>,
      onUploadProgress: OnUploadProgress,
    ) => {
      const uid = user?.uid ?? ''
      if (assessmentId && uid) {
        try {
          const dataPath = `${getPregnancyCoveragePath(coverageId)}.planDocuments`
          const coverageFilePath = `assessments/${assessmentId}/corrections/${dataPath.replace(/\./g, '/')}`
          const prevData =
            coverageId === 'primary'
              ? (corrections?.insuranceInfo?.primaryCoverage?.planDocuments ??
                data?.insuranceInfo?.primaryCoverage?.planDocuments)
              : (corrections?.insuranceInfo?.secondaryCoverage?.planDocuments ??
                data?.insuranceInfo?.secondaryCoverage?.planDocuments)
          const processed = await processListFieldData(
            appName,
            true,
            uid,
            coverageFilePath,
            coverageId === 'primary'
              ? primaryCoverageDocumentsField
              : secondaryCoverageDocumentsField,
            updated,
            prevData,
            onUploadProgress,
            [],
          )
          const m = {}
          set(m, dataPath, processed)
          await updateAssessmentAnswers({ id: assessmentId, data: m, appName })
          return { success: 'Saved!' }
        } catch (e: any) {
          console.error(e)
          return { error: e.message }
        }
      }
      return { error: 'Unknown error' }
    },
    [
      assessmentId,
      appName,
      primaryCoverageDocumentsField,
      secondaryCoverageDocumentsField,
      user,
      corrections,
      data,
    ],
  )

  return (
    <ThemeContext.Provider value={{ placeholderAbove: false }}>
      <Stack w="100%" boxShadow="md" bg="white" spacing={0} borderRadius={6}>
        <InsuranceCardView
          adminView={adminView}
          data={primaryData?.insuranceCard}
          corrections={primaryCorrections?.insuranceCard}
          updateField={(path, data) => handleInsuranceCardUpload('primary', path, data)}
          baseStoragePath={`${baseStoragePath}/primaryCoverage/insuranceCard`}
          field={primaryCoverageInsuranceCardField}
        />
        <DataViewList
          itemField={primaryCoverageDocumentsField.itemFields}
          itemName="Coverage Document"
          initExpanded={!!(primaryData?.planDocuments ?? primaryCorrections?.planDocuments)?.length}
          name="Primary Coverage Documents"
          value={primaryData?.planDocuments}
          correction={primaryCorrections?.planDocuments}
          onSubmit={(data, onUploadProgress) =>
            handleUpdatePlanDocuments('primary', data, onUploadProgress)
          }
          adminView={adminView}
          baseStoragePath={`${baseStoragePath}/primaryCoverage/planDocuments`}
        />
        <InsuranceCardView
          adminView={adminView}
          data={secondaryData?.insuranceCard}
          corrections={secondaryCorrections?.insuranceCard}
          updateField={(path, data) => handleInsuranceCardUpload('secondary', path, data)}
          baseStoragePath={`${baseStoragePath}/secondaryCoverage/insuranceCard`}
          field={secondaryCoverageInsuranceCardField}
        />
        <DataViewList
          itemField={secondaryCoverageDocumentsField.itemFields}
          itemName="Coverage Document"
          name="Secondary Coverage Documents"
          initExpanded={
            !!(secondaryData?.planDocuments ?? secondaryCorrections?.planDocuments)?.length
          }
          adminView={adminView}
          value={secondaryData?.planDocuments}
          correction={secondaryCorrections?.planDocuments}
          onSubmit={(data, onUploadProgress) =>
            handleUpdatePlanDocuments('secondary', data, onUploadProgress)
          }
          baseStoragePath={`${baseStoragePath}/secondaryCoverage/planDocuments`}
        />
      </Stack>
    </ThemeContext.Provider>
  )
}
